/* eslint-disable dot-notation */
import { compose } from 'redux';
import { connect } from 'react-redux';
import { reduxForm, autofill, formValueSelector, change } from 'redux-form';
import { withTranslation } from 'react-i18next';
import { createValidator, passengers } from 'utils/formValidations';
import { passengerCount } from 'utils/Reserbus';
import { setError, setAlert } from '@/actions';
import {
  savePassengersWithTickets,
  formErrors,
  deletePassenger,
  toggleWantsInsurance,
  insurancesChecked,
  clearTicketsErrorType,
} from '@/actions/purchase';
import { fetchProductCatalog } from '@/actions/productsCart';
import Passengers from 'components/purchase/passengers/Passengers';
import purchaserValidations from 'utils/purchaserValidations';
import { setStoredPassenger } from 'utils/storedPassengers';
import { getStoredPurchaser, setStoredPurchaser } from 'utils/storedPurchaser';
import getSafeCountryCallingCode from 'utils/phone';
import { getDoc2BrandMap } from '../../../utils/documentMaps';
import { getUserByWalletType, isAccountData, needsLoyaltySelection } from '../../../utils/loyalty';
import { getInitialPassengers } from '../../../utils/passengers';
import { hasOversoldSeats } from '../../../utils/overbooking';
import { passengerMeetsAgeRequirement } from '../../../utils/passengerUtils';

/**
 * Validator for passenger form
 *
 * @param {Object} values - Form values
 * @returns {Object} - Validation errors
 */
const validate = createValidator({
  ...purchaserValidations,
  passengers: [passengers],
});

/**
 * Check if the date of birth is valid
 * Go through all the passengers, verify if some of them have
 * the bus category "older" or "intermunicipal_free_pass" and, if so, check if those
 * passengers meet the age requirements:
 * - "older" passengers must be 60 years old or older
 * - "intermunicipal_free_pass" passengers must be 65 years old or older
 *
 * Return false if at least one passenger
 * with the "older" or "intermunicipal_free_pass" bus category is not old enough.
 * Otherwise, return true.
 *
 * Return true if no passengers selected the "older" or "intermunicipal_free_pass" bus category.
 *
 * @param {Object} state - The current state.
 * @returns {boolean} - Whether all dates of birth are valid or not.
 */
const datesOfBirthAreValid = (state) => () => {
  const passengers = formValueSelector('passengers')(state, 'passengers');
  const olderPassengers = passengers.filter(
    (passenger) =>
      passenger.busCategory.includes('older') ||
      passenger.busCategory.includes('intermunicipal_free_pass'),
  );

  const validOlderPassengers = olderPassengers.filter(passengerMeetsAgeRequirement);

  return validOlderPassengers.length === olderPassengers.length;
};

/**
 * Validate the pet-friendly quantity.
 * Check if the number of pets taken by passengers with the "pet_friendly" bus category
 * is less than or equal to the number of adults.
 *
 * @param {Object} state - The current state.
 * @returns {boolean} - True if the pet quantity is valid or if no passengers are pet-friendly; false otherwise.
 */
const petFriendlyQuantityIsValid = (state) => () => {
  const passengers = formValueSelector('passengers')(state, 'passengers');
  const adultsCount = passengers.reduce(
    (acc, curr) =>
      curr.busCategory !== 'minor' && curr.busCategory !== 'pet_friendly' ? acc + 1 : acc,
    0,
  );
  const petsTaken = passengers.filter((curr) => curr.busCategory === 'pet_friendly').length;
  return adultsCount >= petsTaken;
};

const mapStateToProps = (state) => {
  const purchase = state.purchase.toJS();
  const {
    form,
    exchange,
    whitelabelConfig: { features, env, copies },
    errors,
    userPreferences,
    reserhubAccount,
  } = state;
  const {
    departs,
    returns,
    passengerSelection,
    passengers,
    busCategories,
    documentId,
    documentType,
    total,
    purchaserFirstName,
    purchaserLastName,
    purchaserSecondLastName,
    phone,
    email,
    phoneCode,
    phoneCountry,
    ticketsErrorType,
    isExchange = false,
    allowsSeatSelection,
    walletType,
    addressAttributes,
    roundTrip,
  } = purchase;

  const user = getUserByWalletType(walletType);

  const getPassengerFormValues = formValueSelector('passengers');
  const storedPurchaser = getStoredPurchaser() || {};
  const phoneCountryValue = env.lang?.default?.substring(3) || 'MX';
  const initialPhoneCountry =
    phoneCountry || (!isExchange ? storedPurchaser.phoneCountry : undefined) || phoneCountryValue;
  const initialPhoneCode = getSafeCountryCallingCode(initialPhoneCountry, phoneCode || '');
  const doc2BrandMapPurchaser = getDoc2BrandMap('purchaser');
  const purchaserDocumentsOptions = Object.values(doc2BrandMapPurchaser);

  // Checking if the first passenger is the same as the account logged in
  const isAccountThePassenger =
    // eslint-disable-next-line prettier/prettier
    user && (!passengers.length || !passengers[0]?.firstName || isAccountData(passengers[0], user));

  const departureOverbooking = hasOversoldSeats(departs.selectedSeats);
  const returnsOverbooking = hasOversoldSeats(returns?.selectedSeats);
  const overbooking = departureOverbooking || returnsOverbooking;

  const overbookingTripType = (() => {
    if (!roundTrip) return undefined;

    if (departureOverbooking && !returnsOverbooking) {
      return 'departure';
    }
    if (returnsOverbooking && !departureOverbooking) {
      return 'returns';
    }
    return undefined; // Both or none, use default title
  })();

  const passengersInitialValues = getInitialPassengers({
    departureSelectedSeats: departs.selectedSeats,
    returnSelectedSeats: returns?.selectedSeats,
    allowsSeatSelection,
    passengerSelection,
    departureTrips: departs.trips,
    returnTrips: returns?.trips || [],
    overbooking,
  });

  return {
    purchase,
    isExchange,
    exchange,
    user,
    passengersForm: form,
    frequentPassengers: userPreferences?.toJS()?.frequentPassengers?.passengers || [],
    busCategories,
    totalPrice: parseFloat(total, 10),
    totalPassengers: passengerCount(passengerSelection),
    ticketsErrorType,
    purchaser: getPassengerFormValues(
      state,
      'purchaserFirstName',
      'purchaserLastName',
      'purchaserSecondLastName',
      'phone',
      'documentType',
      'documentId',
      'email',
    ),
    initialValues: {
      documentId: documentId || (!isExchange ? storedPurchaser.documentId : undefined),
      documentType:
        documentType ||
        (!isExchange ? storedPurchaser.documentType : undefined) ||
        purchaserDocumentsOptions[0] ||
        'CC',
      purchaserFirstName:
        purchaserFirstName || (!isExchange ? storedPurchaser.purchaserFirstName : undefined),
      purchaserLastName:
        purchaserLastName || (!isExchange ? storedPurchaser.purchaserLastName : undefined),
      purchaserSecondLastName:
        purchaserSecondLastName ||
        (!isExchange ? storedPurchaser.purchaserSecondLastName : undefined),
      email: email || (!isExchange ? storedPurchaser.email : undefined),
      phone: phone || (!isExchange ? storedPurchaser.phone : undefined),
      phoneCountry: initialPhoneCountry,
      phoneCode: initialPhoneCode,
      passengers: passengersInitialValues,
      addressAttributes: addressAttributes || storedPurchaser.addressAttributes || {},
    },
    insuranceMessage: state.purchase.getIn(['config', 'insuranceMessage']),
    insuranceAmount: state.purchase.getIn(['config', 'insuranceAmount']),
    insuranceAmountWithTaxes: state.purchase.getIn(['config', 'insuranceAmountWithTaxes']),
    insuranceAmountIsUnitPrice: state.purchase.getIn(['config', 'unitAmount']),
    features,
    env,
    copies,
    datesOfBirthAreValid: datesOfBirthAreValid(state),
    petFriendlyQuantityIsValid: petFriendlyQuantityIsValid(state),
    // This only represents the active loyalty session. Whether the logged account is
    // passenger 0 is tracked separately by isAccountThePassenger.
    isLogged: Boolean(user?.email),
    isAccountThePassenger,
    needsLoyaltySelector: needsLoyaltySelection(),
    isUserAuthenticated: !!(user || reserhubAccount?.toJS()?.user),
    errors,
    overbooking,
    overbookingTripType,
  };
};

const mapDispatchToProps = (dispatch, ownProps) => ({
  savePassengers: (fields, trips) =>
    dispatch(savePassengersWithTickets(ownProps.match.params.token, fields, trips)),

  onToggleWantsInsurance: (token, wants) => dispatch(toggleWantsInsurance(token, wants)),

  onSubmitFail: (fields) => {
    const isAdultRequired = fields.passengers.some(
      (passenger) => passenger.dateOfBirth === 'An adult must be present',
    );
    const msg = isAdultRequired ? 'adult_required' : 'check_the_fields_in_red';
    dispatch(setError(null, msg, 'warning', false, null, null, null, 'fixed'));
    dispatch(formErrors(1, fields));
  },

  autofillFirstPassenger: (
    firstName,
    lastName,
    secondLastName,
    documentType,
    documentId,
    email,
  ) => {
    dispatch(autofill('passengers', 'passengers[0].firstName', firstName));
    dispatch(autofill('passengers', 'passengers[0].lastName', lastName));
    dispatch(autofill('passengers', 'passengers[0].secondLastName', secondLastName));
    // dispatch(autofill('passengers', 'passengers[0].documentType', documentType));
    // dispatch(autofill('passengers', 'passengers[0].documentId', documentId));
    dispatch(autofill('passengers', 'passengers[0].email', email));
  },

  autofillInsurances: (passengerIndex, selectDepartureInsurances, selectReturnsInsurances) => {
    dispatch(
      autofill(
        'passengers',
        `passengers[${passengerIndex}].wantsOutgoingInsurance`,
        selectDepartureInsurances,
      ),
    );
    dispatch(
      autofill(
        'passengers',
        `passengers[${passengerIndex}].wantsIncomingInsurance`,
        selectReturnsInsurances,
      ),
    );
  },

  setInsurancesChecked: () => {
    dispatch(insurancesChecked());
  },

  deletePassenger: (passengerId) => {
    dispatch(deletePassenger(ownProps.match.params.token, passengerId));
  },

  transitionTo: (route) => ownProps.history.push(route),

  returnTo: (route) => ownProps.history.replace(route),

  showDateOfBirthWarning: (key = 'date_of_birth') => dispatch(setAlert(key, 'warning')),
  showIntermunicipalDateOfBirthWarning: () =>
    dispatch(setAlert('intermunicipal_free_pass_date_of_birth', 'warning')),

  showPetFriendlySeatsWarn: () => dispatch(setAlert('adult_per_pet', 'warning')),

  showAcceptTermsWarning: () =>
    dispatch(
      setError(undefined, 'should_accept_terms', 'warning', false, null, null, null, 'fixed'),
    ),

  showLineTermsWarning: () =>
    dispatch(
      setError(undefined, 'should_accept_line_terms', 'warning', false, null, null, null, 'fixed'),
    ),

  clearTicketsErrors: () => {
    dispatch(clearTicketsErrorType());
  },

  fetchProductCatalog: () => dispatch(fetchProductCatalog()),
});

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  reduxForm({
    form: 'passengers',
    /**
     * This redux config setting is optional for control when to validate
     * Since form is rendered conditional we force to validate always something change
     * @returns Boolean is validation should run
     */
    shouldValidate: () => true,
    onChange: (values, dispatch) => {
      const { documentId, documentType, passengers } = values;
      if ((documentType === 'CPF' || documentType === 'CNPJ') && documentId) {
        const valueWithOnlyDigits = documentId.replace(/\D/g, '');
        dispatch(change('passengers', 'documentId', valueWithOnlyDigits));
      }
      passengers.forEach(
        ({ documentId, documentType, documentIdSecond, documentTypeSecond }, index) => {
          if (documentType === 'RG' && documentId) {
            const rgDocumentWithOnlyDigitsAndLetters = documentId.replace(/[^a-zA-Z0-9]/g, '');
            dispatch(
              change(
                'passengers',
                `passengers[${index}].documentId`,
                rgDocumentWithOnlyDigitsAndLetters,
              ),
            );
          }
          if (documentTypeSecond === 'RG' && documentIdSecond) {
            const rgDocumentWithOnlyDigitsAndLetters = documentIdSecond.replace(
              /[^a-zA-Z0-9]/g,
              '',
            );
            dispatch(
              change(
                'passengers',
                `passengers[${index}].documentIdSecond`,
                rgDocumentWithOnlyDigitsAndLetters,
              ),
            );
          }
        },
      );
    },
    onSubmitSuccess: (result, dispatch, props) => {
      // TODO: merge these stored forms
      setStoredPassenger(props.values.passengers);
      setStoredPurchaser({
        ...props.values,
        passengers: undefined,
      });
    },
    validate,
  }),
  withTranslation('passengers'),
)(Passengers);
